import { gsap } from 'gsap/all'
import { throttle } from 'throttle-debounce'

const header = {
  el: {
    header: <HTMLElement>document.querySelector('.header'),
    headerFrontpage: <HTMLElement>document.querySelector('.header--frontpage'),
    headerWhite: <HTMLElement>document.querySelector('.header--white'),
    mobileNavigation: {
      container: <HTMLElement>(
        document.querySelector('.header__mobile-navigation-container')
      ),
      trigger: <HTMLElement>document.querySelector('.mobile-navigation-trigger'),
      items: <NodeListOf<HTMLElement>>(
        document.querySelectorAll(
          '.mobile-navigation__list--open > .mobile-navigation__item, .mobile-navigation__language-switcher'
        )
      ),
      backTriggers: <NodeListOf<HTMLElement>>(
        document.querySelectorAll('.mobile-navigation__link--back')
      ),
      levelTriggers: <NodeListOf<HTMLElement>>(
        document.querySelectorAll(
          '.mobile-navigation .mobile-navigation__item--has-subnavigation > .mobile-navigation__link'
        )
      ),
      languageSwitcher: <HTMLElement>(
        document.querySelector('.mobile-navigation__language-switcher')
      ),
      links: <NodeListOf<HTMLElement>>(
        document.querySelectorAll('.mobile-navigation__link')
      ),
    },
  },

  isMobileNavigationOpen: false,
  lastScrollTop: 0,
  immediateNavigationRender: false,
  currentlyOpenedNavigationLevel: 0,

  init(): void {
    if (!this.el.header) return

    this.handleHeader()
    window.addEventListener('scroll', throttle(15, this.handleHeader).bind(this))

    if (this.el.mobileNavigation.trigger) {
      this.el.mobileNavigation.trigger.addEventListener(
        'click',
        this.toggleMobileNavigation.bind(this)
      )
    }

    this.handleMobileNavigationLevels()

    if (this.el.mobileNavigation.languageSwitcher) {
      this.el.mobileNavigation.languageSwitcher
        .querySelector('select')
        .addEventListener('change', this.handleMobileNavigationLanguage.bind(this))
    }
  },

  handleHeader(): void {
    const currentScrollTop = -document.body.getBoundingClientRect().top
    const scrollingDown = currentScrollTop > this.lastScrollTop ? true : false
    this.lastScrollTop = currentScrollTop

    if (!this.isMobileNavigationOpen) {
      this.toggleHeaderFrontpage(currentScrollTop > window.innerHeight * 0.15)

      if (currentScrollTop > window.innerHeight * 0.75) {
        this.toggleHeaderHidden(scrollingDown)
      } else {
        this.toggleHeaderHidden(false)
      }
    }
  },

  toggleHeaderFrontpage(scrolled): void {
    if (scrolled) {
      if (this.el.headerFrontpage) {
        this.el.headerFrontpage.classList.remove('header--frontpage')
      }
      if (this.el.headerWhite) {
        this.el.headerWhite.classList.remove('header--white')
      }
    } else {
      if (this.el.headerFrontpage) {
        this.el.headerFrontpage.classList.add('header--frontpage')
      }
      if (this.el.headerWhite) {
        this.el.headerWhite.classList.add('header--white')
      }
    }
  },

  toggleHeaderHidden(scrollingDown): void {
    if (scrollingDown) {
      this.el.header.classList.add('header--hidden')
    } else {
      this.el.header.classList.remove('header--hidden')
    }
  },

  toggleMobileNavigation(): void {
    gsap.to(this.el.mobileNavigation.container, {
      clearProps: 'all',
      duration: 0.567,
      ease: 'power4.inOut',
      height: this.isMobileNavigationOpen ? 0 : '100%',
      overwrite: true,
      onStart: () => {
        this.isMobileNavigationOpen = !this.isMobileNavigationOpen

        if (this.isMobileNavigationOpen) {
          this.adjustCurrentNavigationLevel()
          if (this.currentlyOpenedNavigationLevel === 0) {
            gsap.fromTo(
              this.el.mobileNavigation.items,
              {
                opacity: 0,
                y: 32,
              },
              {
                clearProps: 'all',
                delay: 0.345,
                duration: 1.23,
                ease: 'power4.out',
                opacity: 1,
                overwrite: true,
                stagger: 0.12,
                y: 0,
              }
            )
          }
        }

        const elHrs = this.el.mobileNavigation.trigger.querySelectorAll('hr')
        const elHr1 = this.el.mobileNavigation.trigger.querySelector('hr:nth-of-type(1)')
        const elHr2 = this.el.mobileNavigation.trigger.querySelector('hr:nth-of-type(2)')
        const elHr3 = this.el.mobileNavigation.trigger.querySelector('hr:nth-of-type(3)')

        if (this.isMobileNavigationOpen) {
          gsap
            .timeline()
            .set(elHrs, { clearProps: 'all', overwrite: true }, 0)
            .to(elHr1, { duration: 0.2, ease: 'power3.out', overwrite: true, y: 9 }, 0)
            .to(elHr3, { duration: 0.2, ease: 'power3.out', overwrite: true, y: -9 }, 0)
            .set(elHr2, { overwrite: true, scale: 0 }, 0.2)
            .to(elHr1, { duration: 1, ease: 'elastic.out', rotation: -45 }, 0.2)
            .to(elHr3, { duration: 1, ease: 'elastic.out', rotation: 45 }, 0.2)
        } else {
          gsap
            .timeline()
            .set(elHr1, { overwrite: true, rotation: -45, y: 6 }, 0)
            .set(elHr2, { overwrite: true, scale: 0 }, 0)
            .set(elHr3, { overwrite: true, rotation: 45, y: -6 }, 0)
            .to(elHr1, { duration: 0.2, ease: 'power3.out', rotation: 0 }, 0)
            .to(elHr3, { duration: 0.2, ease: 'power3.out', rotation: 0 }, 0)
            .set(elHr2, { clearProps: 'all' }, 0.2)
            .to(elHr1, { clearProps: 'all', duration: 1, ease: 'elastic.out', y: 0 }, 0.2)
            .to(elHr3, { clearProps: 'all', duration: 1, ease: 'elastic.out', y: 0 }, 0.2)
        }

        if (this.isMobileNavigationOpen) {
          this.el.header.classList.add('header--mobile-navigation-open')
          gsap.set('body', { overflow: 'hidden' })
        }
      },
      onComplete: () => {
        if (this.isMobileNavigationOpen) {
          this.el.header.classList.add('header--mobile-navigation-open')
        } else {
          this.el.header.classList.remove('header--mobile-navigation-open')
          gsap.set('body', { clearProps: 'overflow' })
        }
      },
    })
  },

  handleMobileNavigationLevels(): void {
    this.el.mobileNavigation.levelTriggers.forEach((trigger: HTMLElement) => {
      trigger.addEventListener('click', (event) => {
        const currentItems = Array.from(
          trigger.closest('.mobile-navigation__list').children
        )
        const openList = trigger.nextElementSibling
        const openCurrentItem = trigger.parentElement
        const openItems = Array.from(trigger.nextElementSibling.children)
        const initialButtonsPosition = this.el.mobileNavigation.languageSwitcher
          ? this.el.mobileNavigation.languageSwitcher.getBoundingClientRect().top
          : 0
        this.currentlyOpenedNavigationLevel++

        gsap.to(currentItems, {
          duration: this.immediateNavigationRender ? 0 : 0.2,
          ease: 'power2.out',
          opacity: 0,
          overwrite: true,
          stagger: this.immediateNavigationRender ? 0 : 0.05,
          y: -33,
          onComplete: () => {
            openList.classList.add('mobile-navigation__list--open')
            openCurrentItem.classList.add('mobile-navigation__item--open')
            trigger.classList.add('mobile-navigation__link--hidden')

            currentItems.forEach((item: HTMLElement) => {
              gsap.set(item, { clearProps: 'transform,opacity', overwrite: true })
              item.classList.remove('mobile-navigation__item--hidden')
              if (!item.classList.contains('mobile-navigation__item--open'))
                item.classList.add('mobile-navigation__item--hidden')
            })

            gsap.fromTo(
              openItems,
              {
                opacity: 0,
                y: 123,
              },
              {
                clearProps: 'transform,opacity',
                duration: this.immediateNavigationRender ? 0 : 1,
                ease: 'power4.out',
                opacity: 1,
                overwrite: true,
                stagger: this.immediateNavigationRender ? 0 : 0.05,
                y: 0,
              }
            )

            if (this.el.mobileNavigation.languageSwitcher) {
              gsap.fromTo(
                this.el.mobileNavigation.languageSwitcher,
                {
                  y:
                    initialButtonsPosition -
                    this.el.mobileNavigation.languageSwitcher.getBoundingClientRect().top,
                },
                {
                  clearProps: 'transform',
                  duration: 0.75,
                  ease: 'power2.inOut',
                  stagger: -0.05,
                  y: 0,
                }
              )
            }
          },
        })

        event.preventDefault()
      })
    })

    this.el.mobileNavigation.backTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        const currentList = trigger.closest('.mobile-navigation__list')
        const currentItem = currentList.parentElement
        const currentItems = Array.from(currentList.children)
        const currentLink = currentList.previousElementSibling
        const parentItems = Array.from(currentItem.parentElement.children)
        const initialButtonsPosition = this.el.mobileNavigation.languageSwitcher
          ? this.el.mobileNavigation.languageSwitcher.getBoundingClientRect().top
          : 0
        this.currentlyOpenedNavigationLevel--

        gsap.to(currentItems, {
          duration: 0.2,
          ease: 'power2.out',
          opacity: 0,
          overwrite: true,
          stagger: 0.05,
          y: -33,
          onComplete: () => {
            currentList.classList.remove('mobile-navigation__list--open')
            currentItem.classList.remove('mobile-navigation__item--open')
            currentLink.classList.remove('mobile-navigation__link--hidden')

            parentItems.forEach((item: HTMLElement) =>
              item.classList.remove('mobile-navigation__item--hidden')
            )

            gsap.fromTo(
              parentItems,
              {
                opacity: 0,
                y: 123,
              },
              {
                clearProps: 'transform,opacity',
                duration: 1,
                ease: 'power4.out',
                opacity: 1,
                overwrite: true,
                stagger: 0.05,
                y: 0,
              }
            )

            if (this.el.mobileNavigation.languageSwitcher) {
              gsap.fromTo(
                this.el.mobileNavigation.languageSwitcher,
                {
                  y:
                    initialButtonsPosition -
                    this.el.mobileNavigation.languageSwitcher.getBoundingClientRect().top,
                },
                {
                  clearProps: 'transform',
                  duration: 0.75,
                  ease: 'power2.inOut',
                  stagger: 0.05,
                  y: 0,
                }
              )
            }
          },
        })

        event.preventDefault()
      })
    })
  },

  handleMobileNavigationLanguage(event): void {
    window.location = event.target.value
  },

  adjustCurrentNavigationLevel(): void {
    const currentLocation = window.location.pathname
    let locationsArray = []

    const createLocationsArray = (location) => {
      const segments = location.split('/').filter(Boolean)
      const segmentsArray = segments.map((_, index) => {
        return '/' + segments.slice(0, index + 1).join('/') + '/'
      })
      return segmentsArray
    }

    const click = (selector) => {
      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true,
      })

      selector.dispatchEvent(clickEvent)
    }

    locationsArray = createLocationsArray(currentLocation)

    this.immediateNavigationRender = true

    locationsArray.forEach((location) => {
      const selector = this.el.mobileNavigation.container.querySelector(
        `.mobile-navigation__link[href='${location}']`
      )
      const itemHasSubnavigation = selector?.parentElement?.classList.contains(
        'mobile-navigation__item--has-subnavigation'
      )

      if (selector && itemHasSubnavigation) {
        click(selector)
      }
    })

    this.immediateNavigationRender = false
  },
}

export default header
